// src/redux/slices/user.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { setTokens, clearTokens } from "../../utils/auth";
import { API_BASE } from "../../utils/config";
import { imageValidationFetch } from "../../utils/imageValidation";
import dayjs from "dayjs";
import { readRegistrationFailure, registrationNetworkFailure } from "../../utils/registrationErrors";
import { loginNetworkFailure, readLoginFailure } from "../../utils/authErrors";

const baseURL = API_BASE;

/* ----------------------------- Helpers ----------------------------- */
const getAccess = () => localStorage.getItem("access");
const getRefresh = () => localStorage.getItem("refresh");

const buildAuthHeader = () => {
  const token = getAccess();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const isExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
};

/**
 * Centralized fetch that:
 *  - injects latest Authorization header
 *  - on 401, tries one refresh and retries once
 * NOTE: Great for JSON calls — NOT used for multipart in this file anymore.
 */
const apiFetch = async (url, options = {}, dispatch) => {
  const withAuth = {
    ...(options.headers || {}),
    ...buildAuthHeader(),
  };

  const doFetch = (hdrs) => fetch(url, { ...options, headers: hdrs });

  let res = await doFetch(withAuth);

  if (res.status === 401) {
    const action = await dispatch(refreshToken());
    if (refreshToken.fulfilled.match(action)) {
      const retryHeaders = {
        ...(options.headers || {}),
        ...buildAuthHeader(),
      };
      res = await doFetch(retryHeaders);
    }
  }

  return res;
};

const resolveValidateImagesUrl = () => {
  try {
    const parsed = new URL(baseURL, window.location.origin);
    return `${parsed.origin}/api/v1/crew/validate-images-cv/`;
  } catch {
    return "/api/v1/crew/validate-images-cv/";
  }
};

/* --------------------------- Thunks (Auth) -------------------------- */
export const refreshToken = createAsyncThunk(
  "user/refreshToken",
  async (_, thunkAPI) => {
    try {
      const refresh = getRefresh();
      if (!refresh) throw new Error("No refresh token");

      const res = await fetch(`${baseURL}/token/refresh/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });

      if (!res.ok) throw new Error("Refresh failed");

      const data = await res.json();
      const nextRefresh = data.refresh ?? refresh;
      setTokens({ access: data.access, refresh: nextRefresh });
      return { access: data.access };
    } catch (e) {
      clearTokens();
      return thunkAPI.rejectWithValue(e.message || "Refresh failed");
    }
  }
);

export const bootstrapAuth = createAsyncThunk(
  "user/bootstrapAuth",
  async (_, thunkAPI) => {
    const access = getAccess();
    if (!access || isExpired(access)) {
      await thunkAPI.dispatch(refreshToken());
    }
  }
);

export const login = createAsyncThunk(
  "user/login",
  async ({ email, password }, thunkAPI) => {
    try {
      const res = await fetch(`${baseURL.replace(/\/$/, "")}/token/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        return thunkAPI.rejectWithValue(await readLoginFailure(res));
      }

      const data = await res.json();

      setTokens({ access: data.access, refresh: data.refresh });

      let role = null;
      let profile = null;
      try {
        const profileRes = await fetch(`${baseURL}/auth/me/`, {
          headers: { Authorization: `Bearer ${data.access}` },
        });
        if (profileRes.ok) {
          profile = await profileRes.json();
          role = profile.role;
        }
      } catch {}

      if (role) localStorage.setItem("role", role);
      if (profile?.company) localStorage.setItem("company", JSON.stringify(profile.company));

      return { ...data, role, profile };
    } catch (e) {
      return thunkAPI.rejectWithValue(e?.status !== undefined ? e : loginNetworkFailure());
    }
  }
);

/* ------------------------ Thunks (Public Flow) ----------------------- */
export const registerCrew = createAsyncThunk(
  "user/registerCrew",
  async (formValues, thunkAPI) => {
    try {
      const fd = new FormData();

      // Personal
      fd.append("first_name", formValues.firstName);
      fd.append("family_name", formValues.familyName);
      fd.append("phone", formValues.phone);
      fd.append("email", formValues.email);
      fd.append("email_verification_token", formValues.emailVerificationToken || "");
      if (formValues.dob && dayjs(formValues.dob).isValid()) {
        fd.append("dob", dayjs(formValues.dob).format("YYYY-MM-DD"));
      }
      if (formValues.profilePhotoFile) {
        fd.append("profile_photo", formValues.profilePhotoFile);
      }

      // Nationality & ID
      fd.append("nationality", formValues.nationality);
      fd.append("national_id", formValues.nationalId);
      if (formValues.idPhotoFile) fd.append("id_photo", formValues.idPhotoFile);

      // Education
      fd.append("languages", JSON.stringify(formValues.languages || []));
      if (formValues.universityName) fd.append("university_name", formValues.universityName);
      if (formValues.universityMajor) fd.append("university_major", formValues.universityMajor);
      if (formValues.universityYear) fd.append("university_year", formValues.universityYear);

      // Location
      if (formValues.governorate) fd.append("governorate", formValues.governorate);
      if (formValues.address) fd.append("address", formValues.address);

      // Work & Other
      const jobMap = { full: "full_time", part: "part_time", unemployed: "unemployed" };
      if (formValues.jobStatus) fd.append("job_status", jobMap[formValues.jobStatus] || formValues.jobStatus);
      if (formValues.gender) fd.append("gender", formValues.gender);
      const toBool = (v) => String(v ?? "").toLowerCase() === "yes";
      fd.append("driver_license", toBool(formValues.driverLicense));
      fd.append("own_car", toBool(formValues.ownCar));
      if (formValues.heardAboutUs) fd.append("heard_about_us", formValues.heardAboutUs);
      if (Array.isArray(formValues.experience)) {
        fd.append("experience", JSON.stringify(formValues.experience));
      }

      // Credentials
      fd.append("password", formValues.password);

      const res = await imageValidationFetch(`${baseURL}/crew/register/`, {
        method: "POST",
        body: fd,
      });

      if (!res.ok) {
        return thunkAPI.rejectWithValue(await readRegistrationFailure(res));
      }
      return await res.json().catch(() => ({}));
    } catch (e) {
      return thunkAPI.rejectWithValue(e?.status !== undefined ? e : registrationNetworkFailure());
    }
  }
);

/* ------------------------ Thunks (Protected API) --------------------- */
export const fetchCrew = createAsyncThunk(
  "user/fetchCrew",
  async (_, thunkAPI) => {
    try {
      const res = await apiFetch(`${baseURL}/crew/`, {}, thunkAPI.dispatch);
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.detail) msg = j.detail;
        } catch {}
        throw new Error(msg);
      }
      return await res.json();
    } catch (e) {
      return thunkAPI.rejectWithValue(e.message || "Failed to fetch crew");
    }
  },
  {
    condition: (options, { getState }) => {
      const state = getState()?.user;
      if (options?.force) return true;
      if (state?.loading) return false;
      const age = Date.now() - Number(state?.lastFetchedAt || 0);
      return !state?.list?.length || age > 60 * 1000;
    },
  }
);

export const fetchCrewDetail = createAsyncThunk(
  "user/fetchCrewDetail",
  async (id, thunkAPI) => {
    try {
      if (!id) throw new Error("Missing crew member id");
      const res = await apiFetch(`${baseURL}/crew/${id}/`, { cache: "no-store" }, thunkAPI.dispatch);
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const data = await res.json();
          if (data?.detail) msg = data.detail;
        } catch {}
        throw new Error(msg);
      }
      return await res.json();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || "Failed to load crew details");
    }
  }
);

export const changeCrewStatus = createAsyncThunk(
  "user/changeCrewStatus",
  async (payload, thunkAPI) => {
    try {
      const { id, status, tag_codes } = payload || {};
      if (!id) throw new Error("Missing id");
      if (!status) throw new Error("Missing status");

      const body = tag_codes && Array.isArray(tag_codes) && tag_codes.length
        ? { status, tag_codes }
        : { status };

      const res = await apiFetch(
        `${baseURL}/crew/${id}/change_status/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        thunkAPI.dispatch
      );
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.detail) msg = j.detail;
          if (j?.error) msg = j.error;
        } catch {}
        throw new Error(msg);
      }
      return await res.json();
    } catch (e) {
      return thunkAPI.rejectWithValue(e.message || "Failed to change status");
    }
  }
);

export const changeCrewRole = createAsyncThunk(
  "user/changeCrewRole",
  async ({ id, role }, thunkAPI) => {
    try {
      const res = await apiFetch(
        `${baseURL}/crew/${id}/change_role/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ role }),
        },
        thunkAPI.dispatch
      );
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.detail) msg = j.detail;
          if (j?.error) msg = j.error;
        } catch {}
        throw new Error(msg);
      }
      return await res.json();
    } catch (e) {
      return thunkAPI.rejectWithValue(e.message || "Failed to change role");
    }
  }
);

/**
 * PATCH /api/crew/:id/
 * Limited fields; multipart with files. We do NOT use apiFetch here to avoid
 * body reuse issues — we handle 401 refresh and retry locally with a *fresh* FormData.
 */
export const changeCrewDetails = createAsyncThunk(
  "user/changeCrewDetails",
  async ({ id, national_id, is_graduated, own_car, profile_photo, id_photo }, thunkAPI) => {
    try {
      if (!id) throw new Error("Missing id");

      // helper to build FormData fresh each time
      const buildFD = () => {
        const fd = new FormData();
        if (typeof national_id !== "undefined") fd.append("national_id", String(national_id));
        if (typeof is_graduated !== "undefined") fd.append("is_graduated", is_graduated ? "true" : "false");
        if (typeof own_car !== "undefined") fd.append("own_car", own_car ? "true" : "false");
        if (profile_photo instanceof File) fd.append("profile_photo", profile_photo);
        if (id_photo instanceof File) fd.append("id_photo", id_photo);
        return fd;
      };

      // first attempt
      let res = await fetch(`${baseURL}/crew/${id}/`, {
        method: "PATCH",
        headers: { ...buildAuthHeader(), Accept: "application/json" }, // NO 'Content-Type' here
        body: buildFD(),
      });

      if (res.status === 401) {
        const action = await thunkAPI.dispatch(refreshToken());
        if (refreshToken.fulfilled.match(action)) {
          // retry with fresh FormData and fresh header
          res = await fetch(`${baseURL}/crew/${id}/`, {
            method: "PATCH",
            headers: { ...buildAuthHeader(), Accept: "application/json" },
            body: buildFD(),
          });
        }
      }

      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.detail) msg = j.detail;
          if (Array.isArray(j) && j[0]) msg = j[0]; // DRF field error list
        } catch {}
        throw new Error(msg);
      }
      return await res.json();
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message || "Failed to update crew details");
    }
  }
);

/* ------------------------ NEW: validate images (no auto-update) ----- */
export const validateCrewImages = createAsyncThunk(
  "user/validateCrewImages",
  async ({ userIds }, thunkAPI) => {
    try {
      const res = await apiFetch(
        resolveValidateImagesUrl(),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_ids: Array.isArray(userIds) ? userIds : [] }),
        },
        thunkAPI.dispatch
      );
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.detail) msg = j.detail;
          if (j?.error) msg = j.error;
        } catch {}
        throw new Error(msg);
      }
      const data = await res.json();
      return data; // { results: [...] }
    } catch (e) {
      return thunkAPI.rejectWithValue(e.message || "Validation failed");
    }
  }
);

/* ------------------------------ Slice ------------------------------- */
const initialState = {
  list: [],
  lastFetchedAt: 0,
  loading: false,
  error: null,
  auth: { access: localStorage.getItem("access") || null },
  registering: false,
  registerError: null,
  changing: false,
  changeError: null,
  // optional UI flags for validations (you can wire them or ignore)
  validatingImages: false,
  validateImagesError: null,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearLoginError(state) {
      state.error = null;
    },
    logout(state) {
      clearTokens();
      state.auth.access = null;
      state.list = [];
    },
  },
  extraReducers: (b) => {
    // registerCrew
    b.addCase(registerCrew.pending, (s) => {
      s.registering = true;
      s.registerError = null;
    });
    b.addCase(registerCrew.fulfilled, (s) => {
      s.registering = false;
    });
    b.addCase(registerCrew.rejected, (s, a) => {
      s.registering = false;
      s.registerError = a.payload || "Crew registration failed";
    });

    // login
    b.addCase(login.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(login.fulfilled, (s, a) => {
      s.loading = false;
      s.auth.access = a.payload.access;
    });
    b.addCase(login.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload || "Login failed";
    });

    // fetch crew
    b.addCase(fetchCrew.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(fetchCrew.fulfilled, (s, a) => {
      s.loading = false;
      s.list = Array.isArray(a.payload) ? a.payload : [];
      s.lastFetchedAt = Date.now();
    });
    b.addCase(fetchCrew.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload || "Failed to fetch crew";
    });

    b.addCase(fetchCrewDetail.fulfilled, (s, a) => {
      const detail = a.payload;
      const idx = s.list.findIndex((u) => u.id === detail.id);
      if (idx !== -1) s.list[idx] = { ...s.list[idx], ...detail };
    });

    // change status
    b.addCase(changeCrewStatus.pending, (s) => {
      s.changing = true;
      s.changeError = null;
    });
    b.addCase(changeCrewStatus.fulfilled, (s, a) => {
      s.changing = false;
      const updated = a.payload;
      const idx = s.list.findIndex((u) => u.id === updated.id);
      if (idx !== -1) s.list[idx] = { ...s.list[idx], ...updated };
      else s.list.unshift(updated);
    });
    b.addCase(changeCrewStatus.rejected, (s, a) => {
      s.changing = false;
      s.changeError = a.payload || "Failed to change status";
    });

    // change role
    b.addCase(changeCrewRole.pending, (s) => {
      s.changing = true;
      s.changeError = null;
    });
    b.addCase(changeCrewRole.fulfilled, (s, a) => {
      s.changing = false;
      const updated = a.payload;
      const idx = s.list.findIndex((u) => u.id === updated.id);
      if (idx !== -1) s.list[idx] = { ...s.list[idx], ...updated };
      else s.list.unshift(updated);
    });
    b.addCase(changeCrewRole.rejected, (s, a) => {
      s.changing = false;
      s.changeError = a.payload || "Failed to change role";
    });

    // change details (multipart)
    b.addCase(changeCrewDetails.pending, (s) => {
      s.changing = true;
      s.changeError = null;
    });
    b.addCase(changeCrewDetails.fulfilled, (s, a) => {
      s.changing = false;
      const updated = a.payload;
      const idx = s.list.findIndex((u) => u.id === updated.id);
      if (idx !== -1) s.list[idx] = { ...s.list[idx], ...updated };
      else s.list.unshift(updated);
    });
    b.addCase(changeCrewDetails.rejected, (s, a) => {
      s.changing = false;
      s.changeError = a.payload || "Failed to update crew details";
    });

    // validate images
    b.addCase(validateCrewImages.pending, (s) => {
      s.validatingImages = true;
      s.validateImagesError = null;
    });
    b.addCase(validateCrewImages.fulfilled, (s) => {
      s.validatingImages = false;
    });
    b.addCase(validateCrewImages.rejected, (s, a) => {
      s.validatingImages = false;
      s.validateImagesError = a.payload || "Validation failed";
    });

    // refresh
    b.addCase(refreshToken.fulfilled, (s, a) => {
      s.auth.access = a.payload.access;
    });
    b.addCase(refreshToken.rejected, (s) => {
      s.auth.access = null;
    });

    b.addCase(bootstrapAuth.rejected, (s) => {
      s.auth.access = null;
    });
  },
});

export const { clearLoginError, logout } = userSlice.actions;
export default userSlice.reducer;
